// aggregate.js — Pure aggregation engine (spec §8)
import { band, mean, median, mode, TOOL_ORDER, TOOL_LABEL } from './format.js';

/**
 * Filter allProjects by user-selected filters.
 * @param {Array} allProjects
 * @param {{type:'All'|'HRDP'|'FDP', regions:Set<string>, bands:Set<string>}} filters
 */
export function filterProjects(allProjects, filters) {
  const { type, regions, bands } = filters;
  return allProjects.filter((p) => {
    if (type !== 'All' && p.type !== type) return false;
    if (regions && regions.size > 0 && !regions.has(p.region)) return false;
    if (bands && bands.size > 0 && (!p.band || !bands.has(p.band))) return false;
    return true;
  });
}

/**
 * agg(projects) — recomputes every project-level derived block.
 * Pure. No side-effects.
 */
export function agg(projects) {
  const n = projects.length;
  const scored = projects.filter((p) => p.overall !== null && p.overall !== undefined);
  const nScored = scored.length;
  const hrdp = projects.filter((p) => p.type === 'HRDP').length;
  const fdp = projects.filter((p) => p.type === 'FDP').length;

  // Budget
  const funded = projects.filter((p) => p.budgetCr > 0);
  const totalBudget = funded.reduce((a, b) => a + b.budgetCr, 0);
  const meanBudget = funded.length ? totalBudget / funded.length : null;
  const medianBudget = funded.length ? median(funded.map((p) => p.budgetCr)) : null;
  const budgetCov = funded.length;

  // Partners / states
  const partnersSet = new Set();
  const statesSet = new Set();
  projects.forEach((p) => {
    if (p.partner) partnersSet.add(p.partner);
    (p.states || []).forEach((s) => s && statesSet.add(s));
  });

  const po = mean(scored.map((p) => p.overall));

  // Per-criterion averages
  const CRITS = ['Relevance', 'Coherence', 'Efficiency', 'Effectiveness', 'Impact', 'Sustainability', 'Branding'];
  const critAvg = {};
  const hf = {};
  CRITS.forEach((c) => {
    critAvg[c] = mean(projects.map((p) => p.criteria?.[c]).filter((v) => v !== null && v !== undefined));
    hf[c] = {
      HRDP: mean(projects.filter((p) => p.type === 'HRDP').map((p) => p.criteria?.[c])),
      FDP: mean(projects.filter((p) => p.type === 'FDP').map((p) => p.criteria?.[c])),
    };
  });

  // Tool tiles
  const tiles = {};
  TOOL_ORDER.forEach((t) => {
    const withTool = projects.filter((p) => p.tools && p.tools[t] !== undefined && p.tools[t] !== null);
    const s = mean(withTool.map((p) => p.tools[t]));
    tiles[t] = {
      label: TOOL_LABEL[t],
      score: s,
      band: band(s),
      projects: withTool.length,
    };
  });

  // Budget range breakdown
  const tiers = [
    { range: '₹1–3 Cr', min: 1, max: 3 },
    { range: '₹3–5 Cr', min: 3, max: 5 },
    { range: '₹5+ Cr', min: 5, max: Infinity },
  ];
  const budgetRange = tiers.map((t) => {
    const rows = funded.filter((p) => p.budgetCr >= t.min && p.budgetCr < t.max);
    const scoredRows = rows.filter((p) => p.overall !== null);
    return {
      range: t.range,
      n: rows.length,
      totalBudgetCr: rows.reduce((a, b) => a + b.budgetCr, 0),
      avgScore: mean(scoredRows.map((p) => p.overall)),
    };
  });

  // Region perf
  const byRegion = groupBy(projects, (p) => p.region);
  const regionPerf = Object.entries(byRegion).map(([region, rows]) => {
    const scoredR = rows.filter((p) => p.overall !== null);
    const total = scoredR.length || 1;
    const counts = { Excellent: 0, Good: 0, Moderate: 0, Poor: 0 };
    scoredR.forEach((p) => { if (p.band) counts[p.band] = (counts[p.band] || 0) + 1; });
    const bands = {};
    Object.keys(counts).forEach((k) => { bands[k] = (counts[k] / total) * 100; });
    return {
      region,
      n: rows.length,
      zone: mode(rows.map((p) => p.zone)),
      bands,
      avgScore: mean(scoredR.map((p) => p.overall)),
    };
  }).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));

  // States (grouped by exploded states[])
  const stateMap = new Map();
  projects.forEach((p) => {
    (p.states || []).forEach((s) => {
      if (!s) return;
      if (!stateMap.has(s)) stateMap.set(s, []);
      stateMap.get(s).push(p);
    });
  });
  const states = Array.from(stateMap.entries()).map(([state, rows]) => {
    const scoredS = rows.filter((p) => p.overall !== null);
    const avg = mean(scoredS.map((p) => p.overall));
    const partnersHere = [...new Set(rows.map((p) => p.partner).filter(Boolean))];
    const toolsAgg = {};
    ['NRM', 'H&H', 'SDLE', 'PoE'].forEach((tl) => {
      const key = tl === 'H&H' ? 'Health' : (tl === 'PoE' ? 'POE' : tl);
      toolsAgg[tl] = mean(rows.map((p) => p.tools?.[key]).filter((v) => v !== null && v !== undefined));
    });
    return {
      state,
      projects: rows.length,
      avgScore: avg,
      band: band(avg),
      partner: mode(rows.map((p) => p.partner)),
      partnersMore: Math.max(0, partnersHere.length - 1),
      region: mode(rows.map((p) => p.region)),
      zone: mode(rows.map((p) => p.zone)),
      tools: toolsAgg,
      budgetCr: rows.reduce((a, b) => a + (b.budgetCr || 0), 0),
      type: mode(rows.map((p) => p.type)),
    };
  }).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));

  // Partners
  const byPartner = groupBy(projects.filter((p) => p.partner), (p) => p.partner);
  const partners = Object.entries(byPartner).map(([partner, rows]) => {
    const scoredP = rows.filter((p) => p.overall !== null);
    const avg = mean(scoredP.map((p) => p.overall));
    const st = [...new Set(rows.flatMap((p) => p.states || []))].slice(0, 3);
    return {
      partner,
      projects: rows.length,
      avgScore: avg,
      band: band(avg),
      states: st,
      type: mode(rows.map((p) => p.type)),
      budgetCr: rows.reduce((a, b) => a + (b.budgetCr || 0), 0),
      impact: mean(rows.map((p) => p.criteria?.Impact)),
      sustainability: mean(rows.map((p) => p.criteria?.Sustainability)),
    };
  }).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));
  const partnersCount = partnersSet.size;

  // regionAgg / zoneAgg
  const regionAgg = Object.entries(byRegion).map(([unit, rows]) => unitAggregate(unit, rows)).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));
  const byZone = groupBy(projects, (p) => p.zone);
  const zoneMembersMap = {};
  projects.forEach((p) => {
    if (!zoneMembersMap[p.zone]) zoneMembersMap[p.zone] = new Set();
    zoneMembersMap[p.zone].add(p.region);
  });
  const zoneAgg = Object.entries(byZone).map(([unit, rows]) => {
    const u = unitAggregate(unit, rows);
    u.members = [...(zoneMembersMap[unit] || [])];
    return u;
  }).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));

  // Scatter — funded, scored
  const scatter = projects
    .filter((p) => p.budgetCr > 0 && p.overall !== null)
    .map((p) => ({
      code: p.code,
      budgetCr: p.budgetCr,
      overall: p.overall,
      band: p.band,
      partner: p.partner,
      state: p.state,
      type: p.type,
    }));

  // Sorted projects
  const projectsSorted = [...scored].sort((a, b) => (b.overall || 0) - (a.overall || 0));

  return {
    n,
    nScored,
    hrdp,
    fdp,
    totalBudget,
    meanBudget,
    medianBudget,
    budgetCov,
    partnersCount,
    statesCount: statesSet.size,
    po,
    poBand: band(po),
    critAvg,
    hf,
    tiles,
    budgetRange,
    regionPerf,
    states,
    partners,
    regionAgg,
    zoneAgg,
    scatter,
    projectsSorted,
  };
}

function unitAggregate(unit, rows) {
  const scoredU = rows.filter((p) => p.overall !== null);
  const avg = mean(scoredU.map((p) => p.overall));
  const tools = {};
  ['NRM', 'H&H', 'SDLE', 'PoE'].forEach((tl) => {
    const key = tl === 'H&H' ? 'Health' : (tl === 'PoE' ? 'POE' : tl);
    tools[tl] = mean(rows.map((p) => p.tools?.[key]).filter((v) => v !== null && v !== undefined));
  });
  return {
    unit,
    projects: rows.length,
    avgScore: avg,
    band: band(avg),
    tools,
    members: null,
  };
}

function groupBy(arr, keyFn) {
  const m = {};
  arr.forEach((v) => {
    const k = keyFn(v);
    if (k === null || k === undefined) return;
    (m[k] = m[k] || []).push(v);
  });
  return m;
}
